import {useCallback, useState} from 'react';
import {useNavigate} from 'react-router';
import dayjs from 'dayjs';
import {SectionCard} from '@ui/layouts/section-card';
import {Body1, Body2, Headline2} from '@ui/atoms/typography';
import {CustomMarkdown} from '@ui/atoms/typography/markdown';
import {Button} from '@ui/atoms/button';
import {Avatar} from '@ui/atoms/avatar';
import {Image} from '@ui/atoms/image';
import {CancelIcon} from '@ui/atoms/icons';
import {VerticalSpacer} from '@ui/atoms/spacer';
import {ModalCard} from '@ui/layouts/modal-card';
import {BackAction} from '@ui/molecules/back-action';
import {Modal} from '@ui/molecules/modal';
import {DropdownVertical} from '@ui/molecules/dropdown-vertical';
import {DropdownItem} from '@pages/dashboard/you/you-page.styles';
import {parseName} from '../my-checkins-list';
import {usePulseEmoji} from '../hooks/usePulseEmoji';
import {getPulseScaleOption} from '../../shared/pulse-scale';
import {MentionIndicator} from '@ui/layouts/users-goals/comments-style';
import {useFirebaseFetch} from '@hooks/query-hook';
import {useStoreContext} from '@store/store-context';
import {
  checkinSubmissionStatusLabel,
  getCheckinSubmissionStatus,
} from '../../team-management/team-management.utils';

import {
  ISpecificCheckinsWithResponseData,
  ISpecificCheckinsWithoutResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {RotatingLines} from 'react-loader-spinner';

export type SubmissionRecord = Omit<
  ISpecificCheckinsWithoutResponseData,
  'user'
> & {
  submittedAt?: string;
  createdAt?: string;
  cycle?: {id?: string; cycleName?: string};
  cycleName?: string;
  cycleId?: string;
  mood?: string;
  pulse?: {
    mood: string;
    feedback: string | null;
  };
  isExcused?: boolean;
  isSubmitted?: boolean;
  isReviewed?: boolean;
  dueDay?: string;
  dueDate?: string;
  dueAt?: string;
  status?: string;
  availabilityState?: 'scheduled';
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    jobTitle?: string;
    timezone?: string;
    avatar?: {url?: string};
  };
};

interface SubmissionCardProps {
  submission: SubmissionRecord;
  onToggleExpand?: (submissionId: string, userId: string) => void;
  onFetchDetail?: (checkinId: string, userId: string) => Promise<void>;
  onSendReminder?: (submissionId: string, userId: string) => void;
  onMarkAsExcused?: (submissionId: string, userId: string) => void;
  onMarkAsReviewed?: (submissionId: string, userId: string) => void;
  onViewComments?: (submissionId: string, userId: string) => void;
  showReviewActions?: boolean;
  showViewComments?: boolean;
  showViewSubmission?: boolean;
  isExpanded?: boolean;
  isLoadingDetail?: boolean;
  isReviewingSubmission?: boolean;
  isSendingReminder?: boolean;
  detail?: ISpecificCheckinsWithResponseData | null;
  responseText?: string;
  displayMode?: 'teamFeed' | 'history';
  isLatestOccurrence?: boolean;
}

export const SubmissionCard = ({
  submission,
  onToggleExpand,
  onFetchDetail,
  onSendReminder,
  onMarkAsExcused,
  onMarkAsReviewed,
  onViewComments,
  showReviewActions = false,
  showViewComments = false,
  showViewSubmission = true,
  isExpanded = false,
  isLoadingDetail = false,
  isReviewingSubmission = false,
  isSendingReminder = false,
  detail,
  responseText,
  displayMode = 'teamFeed',
  isLatestOccurrence = true,
}: SubmissionCardProps) => {
  const navigate = useNavigate();
  const {emojiMap} = usePulseEmoji();
  const [showExcuseModal, setShowExcuseModal] = useState(false);

  const {
    authStore: {auth},
  } = useStoreContext();

  // Fetch unread mentions for this submission
  const mentionsPath = `users/${auth.user.id}/mentions/checkins/comments/${submission.id}`;
  const {data: mentionsData} = useFirebaseFetch(mentionsPath);

  const unreadCount = mentionsData?.unreadComment || 0;
  const hasMentions = mentionsData?.mentionedBy?.length > 0;

  const handleSendReminder = useCallback(() => {
    if (onSendReminder && submission?.id && submission?.user?.id) {
      onSendReminder(submission.id, submission.user.id);
    }
  }, [onSendReminder, submission?.id, submission?.user?.id]);

  const handleMarkAsExcused = useCallback(() => {
    if (onMarkAsExcused && submission?.id && submission?.user?.id) {
      onMarkAsExcused(submission.id, submission.user.id);
      setShowExcuseModal(false);
    }
  }, [onMarkAsExcused, submission?.id, submission?.user?.id]);

  const handleMarkAsReviewed = useCallback(() => {
    if (onMarkAsReviewed && submission?.id && submission?.user?.id) {
      onMarkAsReviewed(submission.id, submission.user.id);
    }
  }, [onMarkAsReviewed, submission?.id, submission?.user?.id]);

  const handleViewComments = useCallback(() => {
    if (onViewComments && submission?.id && submission?.user?.id) {
      onViewComments(submission.id, submission.user.id);
    }
  }, [onViewComments, submission?.id, submission?.user?.id]);

  const dropdownMenu = (handleClose: () => void) => (
    <div>
      <DropdownItem
        onClick={() => {
          setShowExcuseModal(true);
          handleClose();
        }}>
        Mark as excused
      </DropdownItem>
    </div>
  );

  const handleToggleExpand = useCallback(() => {
    if (onToggleExpand && submission?.id && submission?.user?.id) {
      onToggleExpand(submission.id, submission.user.id);
    }

    if (
      isExpanded === false &&
      onFetchDetail &&
      !detail &&
      submission?.id &&
      submission?.user?.id
    ) {
      onFetchDetail(submission.id, submission.user.id);
    }
  }, [
    onToggleExpand,
    onFetchDetail,
    submission?.id,
    submission?.user?.id,
    isExpanded,
    detail,
  ]);

  if (!submission?.id) {
    return null;
  }

  const submissionStatus = getCheckinSubmissionStatus(
    {
      isSubmitted: submission.isSubmitted,
      submittedAt: submission.submittedAt,
      isExcused: submission.isExcused,
      dueDay: submission.dueDay,
      dueDate: submission.dueDate,
      dueAt: submission.dueAt,
      status: submission.status,
      availabilityState: submission.availabilityState,
    },
    {isLatestOccurrence},
  );
  const isSubmitted = submissionStatus.kind === 'submitted';
  const isExcused = submissionStatus.kind === 'excused';
  const isReviewed = !!submission.isReviewed;

  const submissionId = submission.id;
  const userName = parseName(submission.user);
  const jobTitle = submission?.user?.jobTitle || 'Team member';
  const submittedAt = submission.submittedAt
    ? dayjs(submission.submittedAt).format('MMM D, h:mm A')
    : 'Submission time unavailable';

  const pulseMood = submission?.mood || submission?.pulse?.mood;

  const moodKey = getPulseScaleOption(pulseMood)?.key;

  const moodOption = moodKey ? emojiMap[moodKey] : undefined;

  const getContentForState = () => {
    if (isExcused) {
      return (
        <div className="flex items-center gap-2">
          <Body2 weight="semibold">
            {checkinSubmissionStatusLabel(submissionStatus)}
          </Body2>
        </div>
      );
    }

    if (!isSubmitted) {
      return (
        <div className="flex justify-between items-center">
          <Body2 weight="semibold">
            {checkinSubmissionStatusLabel(submissionStatus)}
          </Body2>
          <div className="flex items-center gap-2">
            {(submissionStatus.kind === 'pending' ||
              submissionStatus.kind === 'overdue') && (
              <Button
                style={{padding: '6px 12px'}}
                onClick={handleSendReminder}
                disabled={isSendingReminder}
                isLoading={isSendingReminder}>
                Send reminder
              </Button>
            )}
            {submissionStatus.kind !== 'scheduled' && (
              <DropdownVertical menu={dropdownMenu} collapseOnClick={true} />
            )}
          </div>
        </div>
      );
    }

    // Submitted state - show normal content
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Body2 weight="semibold">How are things going for you?</Body2>

          {moodOption && (
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 ">
                <Image {...moodOption.emoji} />
              </div>
              <Body2 kind="textBody" weight="semibold">
                ({moodOption.label})
              </Body2>
            </div>
          )}
          {responseText && (
            <Body2 kind="textBody" weight="semibold">
              • {responseText}
            </Body2>
          )}
        </div>

        {isExpanded && (
          <div>
            {detail && (
              <>
                <div className="flex flex-col gap-3">
                  {(() => {
                    const answeredQuestions =
                      detail?.responses?.questions?.questions ?? [];
                    const filtered = answeredQuestions.filter(
                      (question: any) =>
                        question?.response && question.response !== '',
                    );
                    if (filtered.length === 0) {
                      return (
                        <Body2 kind="textBody">
                          No responses captured for this check-in.
                        </Body2>
                      );
                    }
                    return filtered.map((question: any, index: number) => (
                      <div
                        key={question?._id || question?.questionId || index}
                        className="flex flex-col gap-1">
                        <Body2 weight="semibold">{question?.question}</Body2>
                        <CustomMarkdown
                          text={`• ${question?.response}`}
                          compact
                          className="text-[14px] leading-[22px] text-textBody"
                        />
                      </div>
                    ));
                  })()}
                </div>
              </>
            )}
          </div>
        )}

        {isSubmitted && (
          <div className="mt-3">
            <button
              type="button"
              onClick={handleToggleExpand}
              className="text-[#585ADF] text-sm font-semibold hover:underline flex flex-row items-center gap-2">
              {isLoadingDetail && (
                <div className="">
                  <RotatingLines
                    strokeColor="#585ADF"
                    strokeWidth="3"
                    animationDuration="0.75"
                    width="14"
                    visible={true}
                  />
                </div>
              )}

              {isExpanded ? '- Collapse' : '+ Expand'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Only show footer for submitted submissions (not excused, not non-submitted)
  const shouldShowFooter = isSubmitted && !isExcused;

  return (
    <>
      <SectionCard
        key={submission.id}
        contentStyling={{padding: '24px'}}
        CustomHeaderTitle={
          <div>
            {displayMode === 'teamFeed' ? (
              <div className="flex flex-row justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Avatar
                    tooltip={true}
                    userId={submission?.user?.id || submissionId}
                    name={userName}
                    size="lg"
                    timezone={submission?.user?.timezone}
                    src={submission?.user?.avatar?.url}
                  />
                  <div className="flex flex-col">
                    <Body1 weight="bold" kind="textDark">
                      {userName}
                    </Body1>
                    <Body2 kind="textBody">{jobTitle}</Body2>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-row justify-between gap-3">
                <div className="flex flex-col">
                  <Body1 weight="bold" kind="textDark">
                    {dayjs(
                      submission.submittedAt || submission.createdAt,
                    ).format('MMM D, YYYY')}{' '}
                    - {submission.cycleName || submission.cycle?.cycleName}
                  </Body1>
                </div>
              </div>
            )}
          </div>
        }
        footer={
          shouldShowFooter ? (
            <div className="">
              <div className="flex flex-wrap gap-3">
                {showViewSubmission && (
                  <Button
                    kind="secondary"
                    style={{padding: '8px 16px'}}
                    onClick={() =>
                      navigate(`/view-checkins/${submissionId}/direct`, {
                        state: {user: submission?.user?.id},
                      })
                    }>
                    View submission
                  </Button>
                )}

                {showViewComments && (
                  <Button
                    kind="secondary"
                    style={{
                      padding: '8px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                    onClick={handleViewComments}>
                    {unreadCount > 0 && (
                      <MentionIndicator
                        style={{
                          background: hasMentions ? '#D52A2A' : '#ededf2',
                          width: '20px',
                          height: '20px',
                        }}>
                        <Body2
                          kind={hasMentions ? 'white' : 'textBody'}
                          style={{fontSize: '12px', fontWeight: 500}}>
                          {unreadCount}
                        </Body2>
                      </MentionIndicator>
                    )}
                    View comments
                  </Button>
                )}

                {showReviewActions && (
                  <Button
                    kind="secondary"
                    style={{padding: '8px 16px'}}
                    disabled={isReviewed || isReviewingSubmission}
                    isLoading={isReviewingSubmission}
                    onClick={handleMarkAsReviewed}>
                    Mark as reviewed
                  </Button>
                )}
              </div>
            </div>
          ) : undefined
        }
        action={
          isSubmitted ? (
            <div className="bg-[#F6F6F8] px-2 py-1 rounded-lg">
              <Body2 kind="textBody" weight="semibold">
                Submitted {submittedAt}
              </Body2>
            </div>
          ) : undefined
        }>
        {getContentForState()}
      </SectionCard>

      {showExcuseModal && (
        <Modal open={true} onClose={() => setShowExcuseModal(false)}>
          <div style={{maxWidth: '500px', margin: 'auto'}}>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
              <BackAction
                width="md"
                icon={<CancelIcon />}
                onClick={() => setShowExcuseModal(false)}
                title="Close"
              />
            </div>
            <VerticalSpacer size="16px" />
            <ModalCard align="center" title={``}>
              <div>
                <VerticalSpacer size="16px" />
                <span>
                  <Headline2 align="center">
                    Mark {userName} as excused
                  </Headline2>
                </span>
                <VerticalSpacer size="16px" />
                <Body1 kind="textBody" align="center">
                  This check-in will no longer count toward participation.
                </Body1>
                <VerticalSpacer size="16px" />

                <Button onClick={handleMarkAsExcused} width="full">
                  Mark as excused
                </Button>
              </div>
            </ModalCard>
          </div>
        </Modal>
      )}
    </>
  );
};
